"use client"

import { init } from "@instantdb/react"
import { schema } from "./schema"

const APP_ID = process.env.NEXT_PUBLIC_INSTANTDB_APP_ID?.trim()
const IS_MOCK_MODE = !APP_ID || APP_ID === "placeholder_app_id" || APP_ID === ""

let clearedStaleDb = false
let reloadScheduled = false
const RELOAD_COOLDOWN = 5000 // 5 seconds cooldown between reloads

const clearStaleDb = () => {
  if (clearedStaleDb || !APP_ID || reloadScheduled) return
  
  // Check if we've reloaded recently (within cooldown period)
  const lastReload = sessionStorage.getItem("instantdb-last-reload")
  if (lastReload) {
    const timeSinceReload = Date.now() - parseInt(lastReload, 10)
    if (timeSinceReload < RELOAD_COOLDOWN) {
      console.warn("⚠️ [InstantDB] Skipping reload - too soon since last reload")
      return
    }
  }
  
  try {
    clearedStaleDb = true
    reloadScheduled = true
    indexedDB.deleteDatabase(APP_ID)
    console.warn("⚠️ [InstantDB] Cleared stale IndexedDB to recover from missing object store")
    // Mark reload time and schedule reload
    sessionStorage.setItem("instantdb-last-reload", Date.now().toString())
    setTimeout(() => {
      if (reloadScheduled) {
        reloadScheduled = false
        window.location.reload()
      }
    }, 1000) // Increased delay to 1 second
  } catch (error) {
    console.warn("⚠️ [InstantDB] Failed to clear stale IndexedDB", error)
    reloadScheduled = false
  }
}

if (typeof window !== "undefined") {
  // Swallow IndexedDB "object store not found" errors so the UI doesn't crash
  const swallowIndexedDbError = (error: any) => {
    const msg = error?.message || ""
    return (
      msg.includes("IDBDatabase") ||
      msg.includes("object stores") ||
      error?.name === "NotFoundError"
    )
  }

  window.addEventListener(
    "error",
    (event) => {
      if (swallowIndexedDbError(event.error)) {
        // Only clear/reload if we haven't already done so
        if (!reloadScheduled) {
          clearStaleDb()
        }
        event.preventDefault()
        console.warn("⚠️ [InstantDB] Ignored IndexedDB error:", event.error?.message)
      }
    },
    true,
  )

  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason: any = event.reason
      if (swallowIndexedDbError(reason)) {
        // Only clear/reload if we haven't already done so
        if (!reloadScheduled) {
          clearStaleDb()
        }
        event.preventDefault()
        console.warn("⚠️ [InstantDB] Ignored unhandled IndexedDB rejection:", reason?.message)
      }
    },
    true,
  )

  console.log(`🔧 [InstantDB] Mode: ${IS_MOCK_MODE ? "MOCK" : "REAL"}, APP_ID: "${APP_ID ?? "unset"}"`)
}

type InstantClient = ReturnType<typeof init>

const createMockDB = (): InstantClient =>
  ({
    useAuth: () => ({ user: null, isLoading: false }),
    useQuery: () => ({
      data: null,
      error: null,
      isLoading: false,
    }),
    auth: {
      async sendMagicCode({ email }: { email: string }) {
        console.log(`🔐 [MOCK] Magic code for ${email}: 000000`)
        return { code: "000000" }
      },
      async signInWithMagicCode({ email }: { email: string; code: string }) {
        console.log(`✅ [MOCK] Signed in as ${email}`)
        return { user: { id: `mock-${Date.now()}`, email } }
      },
      async signOut() {
        console.log("👋 [MOCK] Signed out")
      },
    },
    async query() {
      return { data: {} }
    },
    async transact() {},
    tx: new Proxy(
      {},
      {
        get() {
          return new Proxy(
            {},
            {
              get() {
                return () => ({ update: () => ({}) })
              },
            },
          )
        },
      },
    ),
  }) as unknown as InstantClient

const createRealDb = (): InstantClient => {
  try {
    return init<any>({
      appId: APP_ID as string,
      schema: schema as any,
    })
  } catch (err: any) {
    console.error("⚠️ [InstantDB] Failed to initialize, falling back to mock:", err)
    return createMockDB()
  }
}

// Wrap query and transact methods to catch IndexedDB errors
// Note: We don't wrap useAuth and useQuery hooks as that breaks React hook rules
const wrapWithErrorHandling = (dbClient: InstantClient): InstantClient => {
  if (IS_MOCK_MODE) return dbClient

  const originalQuery = (dbClient as any).query
  const originalTransact = (dbClient as any).transact

  const shouldFallback = (err: any) => {
    const msg = err?.message || ""
    return (
      msg.includes("IDBDatabase") ||
      msg.includes("object store") ||
      err?.name === "NotFoundError"
    )
  }

  if (typeof originalQuery === "function") {
    ;(dbClient as any).query = async (...args: any[]) => {
      try {
        return await originalQuery.apply(dbClient, args)
      } catch (err: any) {
        if (shouldFallback(err)) {
          console.warn("⚠️ [InstantDB] query IndexedDB error, returning empty result:", err?.message)
          return { data: {}, error: null }
        }
        throw err
      }
    }
  }

  if (typeof originalTransact === "function") {
    ;(dbClient as any).transact = async (...args: any[]) => {
      try {
        return await originalTransact.apply(dbClient, args)
      } catch (err: any) {
        if (shouldFallback(err)) {
          console.warn("⚠️ [InstantDB] transact IndexedDB error, ignoring transaction:", err?.message)
          return {}
        }
        throw err
      }
    }
  }
  
  return dbClient
}

const baseDb: InstantClient = IS_MOCK_MODE ? createMockDB() : wrapWithErrorHandling(createRealDb())

// Wrap auth methods to gracefully fall back to mock on IndexedDB store errors
const makeSafeAuth = (client: InstantClient): InstantClient => {
  if (IS_MOCK_MODE) return client

  const mock = createMockDB()
  const shouldFallback = (err: any) => {
    const msg = err?.message || ""
    return (
      msg.includes("IDBDatabase") ||
      msg.includes("object store") ||
      err?.name === "NotFoundError"
    )
  }

  const wrap = <T extends (...args: any[]) => any>(fn: T, mockFn: T): T => {
    return (async (...args: any[]) => {
      try {
        return await fn(...args)
      } catch (err: any) {
        if (shouldFallback(err)) {
          console.warn("⚠️ [InstantDB] Auth falling back to mock due to IndexedDB error:", err?.message)
          return await mockFn(...args)
        }
        throw err
      }
    }) as T
  }

  const auth = (client as any).auth
  const mockAuth = (mock as any).auth
  if (auth && mockAuth) {
    if (typeof auth.sendMagicCode === "function" && typeof mockAuth.sendMagicCode === "function") {
      auth.sendMagicCode = wrap(auth.sendMagicCode.bind(auth), mockAuth.sendMagicCode.bind(mockAuth))
    }
    if (typeof auth.signInWithMagicCode === "function" && typeof mockAuth.signInWithMagicCode === "function") {
      auth.signInWithMagicCode = wrap(auth.signInWithMagicCode.bind(auth), mockAuth.signInWithMagicCode.bind(mockAuth))
    }
    if (typeof auth.signOut === "function" && typeof mockAuth.signOut === "function") {
      auth.signOut = wrap(auth.signOut.bind(auth), mockAuth.signOut.bind(mockAuth))
    }
  }

  return client
}

export const db: InstantClient = makeSafeAuth(baseDb)
export default db
 
